/**
 * T265 — Player profile editor.
 *
 * Route: /account/player-profile/
 *
 * Player edits their canonical Player row — name, bio, photo,
 * region, formats, role, batting/bowling style, daily rate, contact.
 * Toggle "List publicly" to appear on /find-a-player/.
 *
 * Auto-creates the canonical Player on first GET so the editor
 * always has a target.
 */
import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
  AlertCircle, ChevronLeft, ExternalLink, Eye, EyeOff, Upload,
} from "lucide-react";

import {
  getMyPlayerProfile, updateMyPlayerProfile, uploadMyPlayerPhoto,
} from "../../api/coredata.api";
import CareerStatsBlock from "../../components/players/CareerStatsBlock";


const FORMATS = ["T20", "ODI", "TEST", "HUNDRED", "T10", "T15"];

const ROLES = [
  { value: "",              label: "—" },
  { value: "BATTER",        label: "Batter" },
  { value: "BOWLER",        label: "Bowler" },
  { value: "ALL_ROUNDER",   label: "All-rounder" },
  { value: "WICKET_KEEPER", label: "Wicket keeper" },
];

const BOWLING_TYPES = [
  { value: "",       label: "—" },
  { value: "PACE",   label: "Pace" },
  { value: "MEDIUM", label: "Medium pace" },
  { value: "SPIN",   label: "Spin" },
];

const HANDS = [
  { value: "",      label: "—" },
  { value: "RIGHT", label: "Right" },
  { value: "LEFT",  label: "Left" },
];


export default function PlayerProfile() {
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [saveStatus, setSaveStatus] = useState("");
  const [error, setError] = useState("");
  const fileRef = useRef(null);

  async function load() {
    setLoading(true);
    try {
      const { data } = await getMyPlayerProfile();
      setProfile(data);
    } catch (err) {
      setError(err?.response?.data?.detail || "Failed to load profile.");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { load(); }, []);

  function setField(name, value) {
    setProfile((p) => ({ ...p, [name]: value }));
  }

  function toggleFormat(f) {
    setProfile((p) => {
      const cur = p.formats_played || [];
      return {
        ...p,
        formats_played: cur.includes(f)
          ? cur.filter((x) => x !== f)
          : [...cur, f],
      };
    });
  }

  async function save() {
    setError("");
    setSaveStatus("Saving…");
    try {
      const { data } = await updateMyPlayerProfile({
        first_name:    profile.first_name,
        last_name:     profile.last_name,
        nick_name:     profile.nick_name,
        bio:           profile.bio,
        city:          profile.city,
        state:         profile.state,
        role:          profile.role || null,
        batting_hand:  profile.batting_hand || null,
        bowling_hand:  profile.bowling_hand || null,
        bowling_type:  profile.bowling_type || null,
        formats_played: profile.formats_played || [],
        daily_rate_inr: profile.daily_rate_inr ? parseInt(profile.daily_rate_inr, 10) : null,
        email:         profile.email,
        mobile:        profile.mobile,
        is_publicly_listed: profile.is_publicly_listed,
      });
      setProfile(data);
      setSaveStatus("Saved");
      setTimeout(() => setSaveStatus(""), 2000);
    } catch (err) {
      setError(err?.response?.data?.detail || "Save failed.");
      setSaveStatus("");
    }
  }

  async function onPhotoChange(e) {
    const f = e.target.files?.[0];
    if (!f) return;
    setBusy(true);
    setError("");
    try {
      const { data } = await uploadMyPlayerPhoto(f);
      setProfile((p) => ({ ...p, photo_url: data.photo_url }));
    } catch (err) {
      setError(err?.response?.data?.detail || "Photo upload failed.");
    } finally {
      setBusy(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  }

  async function togglePublish() {
    const newVal = !profile.is_publicly_listed;
    setProfile((p) => ({ ...p, is_publicly_listed: newVal }));
    try {
      const { data } = await updateMyPlayerProfile({
        is_publicly_listed: newVal,
      });
      setProfile(data);
    } catch (err) {
      // revert on failure
      setProfile((p) => ({ ...p, is_publicly_listed: !newVal }));
      setError(err?.response?.data?.detail || "Toggle failed.");
    }
  }

  if (loading) return <div className="p-4 text-sm text-text-secondary">Loading…</div>;
  if (!profile) {
    return (
      <div className="p-4">
        <p className="text-sm text-rose-400">{error || "Couldn't load profile."}</p>
      </div>
    );
  }

  // Shared form-input styling. Mirrors the rest of the SPA (Signup,
  // dashboard, etc.) so dark theme renders the same everywhere and
  // text contrast against bg-bg-main meets the existing palette.
  const inputCls =
    "mt-1 w-full px-3 py-2 text-sm rounded-lg bg-bg-main text-text-primary " +
    "border border-bg-secondary focus:border-button-primary outline-none transition-colors " +
    "placeholder:text-text-secondary/60";
  const cardCls =
    "rounded-lg border border-bg-secondary bg-bg-primary p-4";

  return (
    <div className="max-w-3xl mx-auto p-4 sm:p-6 text-text-primary">
      <Link
        to="/dashboard"
        className="inline-flex items-center text-sm text-text-secondary hover:text-text-primary mb-3 transition-colors"
      >
        <ChevronLeft className="w-4 h-4" /> Back to dashboard
      </Link>

      <h1 className="text-xl sm:text-2xl font-bold mb-1 text-text-primary">My player profile</h1>
      <p className="text-sm text-text-secondary mb-4">
        Your public page on the Find-a-Player marketplace. Scouts, coaches,
        and tournament organisers can find you here once you publish.
      </p>

      {/* Publish toggle */}
      <div
        className={`rounded-lg border p-3 mb-4 ${
          profile.is_publicly_listed
            ? "border-emerald-500/40 bg-emerald-500/10"
            : "border-bg-secondary bg-bg-secondary/40"
        }`}
      >
        <div className="flex items-start gap-2">
          {profile.is_publicly_listed
            ? <Eye className="w-5 h-5 text-emerald-400 mt-0.5 shrink-0" />
            : <EyeOff className="w-5 h-5 text-text-secondary mt-0.5 shrink-0" />}
          <div className="flex-1 min-w-0">
            <div className="font-semibold text-text-primary">
              {profile.is_publicly_listed
                ? "Public — listed on Find-a-Player"
                : "Private — not visible to anyone"}
            </div>
            <p className="text-sm text-text-secondary mb-2">
              {profile.is_publicly_listed
                ? "Scouts and tournament organisers can find your profile."
                : "Publish your profile so scouts can find you."}
            </p>
            <div className="flex gap-2 flex-wrap">
              <button
                onClick={togglePublish}
                className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                  profile.is_publicly_listed
                    ? "bg-bg-secondary text-text-primary hover:bg-bg-main border border-bg-secondary"
                    : "bg-button-primary text-white hover:opacity-90"
                }`}
              >
                {profile.is_publicly_listed ? "Unpublish" : "Publish my profile"}
              </button>
              {profile.is_publicly_listed && (
                <Link
                  to={`/find-a-player/${profile.id}`}
                  target="_blank"
                  className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg bg-bg-secondary text-text-primary border border-bg-secondary hover:bg-bg-main text-sm transition-colors"
                >
                  <ExternalLink className="w-3.5 h-3.5" /> View public page
                </Link>
              )}
            </div>
          </div>
        </div>
      </div>

      {profile.other_claimed_count > 0 && (
        <div className="rounded-lg border border-amber-500/40 bg-amber-500/10 p-3 mb-4 text-sm text-amber-200">
          You have {profile.other_claimed_count} other claimed Player records.
          Reply to a support email and we'll merge them into this canonical
          profile.
        </div>
      )}

      {error && (
        <div className="flex items-start gap-2 p-2 bg-rose-500/10 border border-rose-500/40 rounded-lg text-sm text-rose-300 mb-3">
          <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" /> {error}
        </div>
      )}

      {/* T267 — Career stats roll-up. Encouraging signal for the player
          to keep their profile updated as stats accumulate. */}
      <CareerStatsBlock stats={profile.career_stats} />

      <div className="space-y-4">
        {/* Photo */}
        <div className={cardCls}>
          <h2 className="font-semibold mb-2 text-text-primary">Profile photo</h2>
          <div className="flex items-center gap-4">
            {profile.photo_url
              ? <img src={profile.photo_url} alt="" className="w-24 h-24 rounded-full object-cover border border-bg-secondary" />
              : <div className="w-24 h-24 rounded-full bg-bg-main border border-bg-secondary flex items-center justify-center text-text-secondary text-xs">No photo</div>}
            <div>
              <input ref={fileRef} type="file" accept="image/jpeg,image/png,image/webp" onChange={onPhotoChange} className="hidden" />
              <button
                onClick={() => fileRef.current?.click()}
                disabled={busy}
                className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg bg-bg-secondary text-text-primary border border-bg-secondary hover:bg-bg-main text-sm transition-colors disabled:opacity-50"
              >
                <Upload className="w-3.5 h-3.5" /> Upload photo
              </button>
              <p className="text-xs text-text-secondary mt-1">JPG / PNG / WebP, max 4 MB.</p>
            </div>
          </div>
        </div>

        {/* Basics */}
        <div className={cardCls}>
          <h2 className="font-semibold mb-3 text-text-primary">Basics</h2>
          <div className="grid sm:grid-cols-2 gap-3">
            <label className="block">
              <span className="text-sm font-medium text-text-secondary">First name</span>
              <input type="text" value={profile.first_name || ""}
                     onChange={(e) => setField("first_name", e.target.value)}
                     className={inputCls} />
            </label>
            <label className="block">
              <span className="text-sm font-medium text-text-secondary">Last name</span>
              <input type="text" value={profile.last_name || ""}
                     onChange={(e) => setField("last_name", e.target.value)}
                     className={inputCls} />
            </label>
            <label className="block sm:col-span-2">
              <span className="text-sm font-medium text-text-secondary">Nickname (optional)</span>
              <input type="text" value={profile.nick_name || ""}
                     onChange={(e) => setField("nick_name", e.target.value)}
                     className={inputCls} />
            </label>
            <label className="block sm:col-span-2">
              <span className="text-sm font-medium text-text-secondary">Bio</span>
              <textarea rows={4} maxLength={2000} value={profile.bio || ""}
                        onChange={(e) => setField("bio", e.target.value)}
                        className={inputCls}
                        placeholder="Where you grew up scoring, notable tournaments, any career highlights…" />
              <span className="text-xs text-text-secondary">{(profile.bio || "").length}/2000</span>
            </label>
          </div>
        </div>

        {/* Location */}
        <div className={cardCls}>
          <h2 className="font-semibold mb-2 text-text-primary">Location</h2>
          <div className="grid sm:grid-cols-2 gap-3">
            <label className="block">
              <span className="text-sm font-medium text-text-secondary">City</span>
              <input type="text" value={profile.city || ""}
                     onChange={(e) => setField("city", e.target.value)}
                     className={inputCls} />
            </label>
            <label className="block">
              <span className="text-sm font-medium text-text-secondary">State</span>
              <input type="text" value={profile.state || ""}
                     onChange={(e) => setField("state", e.target.value)}
                     className={inputCls} />
            </label>
          </div>
        </div>

        {/* Playing style */}
        <div className={cardCls}>
          <h2 className="font-semibold mb-3 text-text-primary">Playing style</h2>
          <div className="grid sm:grid-cols-2 gap-3">
            <label className="block">
              <span className="text-sm font-medium text-text-secondary">Primary role</span>
              <select value={profile.role || ""} onChange={(e) => setField("role", e.target.value)}
                      className={inputCls}>
                {ROLES.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
              </select>
            </label>
            <label className="block">
              <span className="text-sm font-medium text-text-secondary">Batting hand</span>
              <select value={profile.batting_hand || ""} onChange={(e) => setField("batting_hand", e.target.value)}
                      className={inputCls}>
                {HANDS.map((h) => <option key={h.value} value={h.value}>{h.label}</option>)}
              </select>
            </label>
            <label className="block">
              <span className="text-sm font-medium text-text-secondary">Bowling hand</span>
              <select value={profile.bowling_hand || ""} onChange={(e) => setField("bowling_hand", e.target.value)}
                      className={inputCls}>
                {HANDS.map((h) => <option key={h.value} value={h.value}>{h.label}</option>)}
              </select>
            </label>
            <label className="block">
              <span className="text-sm font-medium text-text-secondary">Bowling type</span>
              <select value={profile.bowling_type || ""} onChange={(e) => setField("bowling_type", e.target.value)}
                      className={inputCls}>
                {BOWLING_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
              </select>
            </label>
          </div>
        </div>

        {/* Formats */}
        <div className={cardCls}>
          <h2 className="font-semibold mb-2 text-text-primary">Formats you play</h2>
          <div className="flex flex-wrap gap-2">
            {FORMATS.map((f) => {
              const on = (profile.formats_played || []).includes(f);
              return (
                <button
                  key={f}
                  type="button"
                  onClick={() => toggleFormat(f)}
                  className={`px-3 py-1.5 rounded-lg border text-sm font-medium transition-colors ${
                    on
                      ? "bg-button-primary text-white border-button-primary"
                      : "bg-bg-secondary text-text-primary border-bg-secondary hover:bg-bg-main"
                  }`}
                >
                  {f}
                </button>
              );
            })}
          </div>
        </div>

        {/* Rate + contact */}
        <div className={cardCls}>
          <h2 className="font-semibold mb-3 text-text-primary">Rate &amp; contact</h2>
          <div className="grid sm:grid-cols-2 gap-3">
            <label className="block">
              <span className="text-sm font-medium text-text-secondary">Day rate (₹) — optional</span>
              <input type="number" min="0" value={profile.daily_rate_inr || ""}
                     onChange={(e) => setField("daily_rate_inr", e.target.value)}
                     placeholder="Net practice / exhibition / coaching"
                     className={inputCls} />
            </label>
            <label className="block">
              <span className="text-sm font-medium text-text-secondary">Contact email</span>
              <input type="email" value={profile.email || ""}
                     onChange={(e) => setField("email", e.target.value)}
                     className={inputCls} />
            </label>
            <label className="block">
              <span className="text-sm font-medium text-text-secondary">Contact phone</span>
              <input type="tel" value={profile.mobile || ""}
                     onChange={(e) => setField("mobile", e.target.value)}
                     className={inputCls} />
            </label>
          </div>
        </div>

        {/* Save bar */}
        <div className="sticky bottom-0 bg-bg-primary border-t border-bg-secondary -mx-4 sm:-mx-6 px-4 sm:px-6 py-3 flex items-center justify-between gap-3 backdrop-blur supports-[backdrop-filter]:bg-bg-primary/95">
          <span className="text-sm text-text-secondary">{saveStatus}</span>
          <button
            onClick={save}
            className="px-4 py-2 rounded-lg bg-button-primary text-white text-sm font-medium hover:opacity-90 transition-opacity"
          >
            Save changes
          </button>
        </div>
      </div>
    </div>
  );
}
